
import { useRef, useState, useCallback } from 'react';
import {
  SwipeConfig,
  SwipeEvents,
  SwipeDirection,
  SwipeEndInfo,
  SwipeUpdateInfo,
  UseSwipeReturn,
} from './Swipe.model';
import {
  mergeSwipeConfig,
  calculateSwipeDirection,
  calculateSwipeProgress,
} from './swipeConfig';
import { calculateOffset } from './Swipe.utils';

export type { UseSwipeReturn };

/**
 * Hook for handling swipe gestures
 *
 * @param config - Swipe configuration (optional, uses defaults)
 * @param events - Events to handle different swipe phases
 * @returns Object with motionProps and additional information
 *
 * @example
 * ```tsx
 * const { motionProps } = useSwipe(
 *   { threshold: 0.3 },
 *   { onThresholdReached: (dir) => console.log('Swiped', dir) }
 * );
 *
 * <motion.div {...motionProps}>Content</motion.div>
 * ```
 */
export const useSwipe = (
  config?: Partial<SwipeConfig>,
  events?: SwipeEvents
): UseSwipeReturn => {
  const finalConfig = mergeSwipeConfig(config);
  const dragOffset = useRef({ x: 0, y: 0 });

  const [animateToOriginal, setAnimateToOriginal] = useState(true);

  const handleDragStart = useCallback(() => {
    events?.onSwipeStart?.();
  }, [events]);

  const handleUpdate = useCallback(
    (latest: any) => {
      const x = Number(latest?.x || 0);
      const y = Number(latest?.y || 0);

      dragOffset.current = { x, y };

      const offset = calculateOffset(x, y, finalConfig.direction);

      if (events?.onSwipeUpdate) {
        const updateInfo: SwipeUpdateInfo = { x, y, offset };
        events.onSwipeUpdate(updateInfo);
      }

      if (events?.onSwipeProgress) {
        const screenSize = finalConfig.direction === 'x' 
          ? window.innerWidth 
          : window.innerHeight;

        const progress = calculateSwipeProgress(
          offset,
          finalConfig.threshold,
          screenSize
        );

        events.onSwipeProgress(progress);
      }
    },
    [events, finalConfig.direction, finalConfig.threshold]
  );

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: any) => {
      setAnimateToOriginal(false);

      const x = dragOffset.current.x;
      const y = dragOffset.current.y;

      const screenSize = finalConfig.direction === 'x' 
        ? window.innerWidth 
        : window.innerHeight;

      const offset = calculateOffset(x, y, finalConfig.direction);

      const thresholdPx = screenSize * finalConfig.threshold;
      const thresholdReached = offset > thresholdPx;

      const direction: SwipeDirection = calculateSwipeDirection(
        x,
        y,
        finalConfig.direction
      );

      if (thresholdReached && events?.onThresholdReached) {
        events.onThresholdReached(direction);
      }

      if (events?.onSwipeEnd) {
        const endInfo: SwipeEndInfo = {
          offset,
          velocity: info?.velocity?.[finalConfig.direction === 'y' ? 'y' : 'x'] || 0,
          direction,
          thresholdReached,
        };
        events.onSwipeEnd(endInfo);
      }

      setAnimateToOriginal(true);
    },
    [events, finalConfig.direction, finalConfig.threshold]
  );

  const motionProps = {
    initial: { x: 0, y: 0 },
    animate: {
      x: animateToOriginal ? 0 : dragOffset.current.x,
      y: animateToOriginal ? 0 : dragOffset.current.y,
      transition: {
        type: 'tween' as const,
        duration: finalConfig.duration,
        ease: 'easeOut',
      },
    },
    drag: (finalConfig.enabled ? finalConfig.direction : false) as 'x' | 'y' | boolean,
    dragConstraints: finalConfig.dragConstraints,
    dragElastic: finalConfig.dragElastic,
    dragDirectionLock: finalConfig.dragDirectionLock,
    onDragStart: handleDragStart,
    onUpdate: handleUpdate,
    onDragEnd: handleDragEnd,
  };

  return {
    motionProps,
    dragOffset: dragOffset.current,
    config: finalConfig,
  };
};
